import React, { useEffect, useState, useCallback } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import VendorProfilePreview from './VendorProfilePreview';
import { VENDOR_TEMPLATE_SHORT } from '../lib/vendorProfileTemplate';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { 
  Store, Package, DollarSign, ShoppingCart, Plus, Edit, Trash2, 
  AlertCircle, CheckCircle, Clock, BarChart3, BadgeCheck, Shield,
  FileCheck, Upload, MessageSquare, Star, HelpCircle, Eye
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth, API } from '../App';
import QuotesManager from '../components/QuotesManager';
import ShareStoreToolkit from '../components/ShareStoreToolkit';
import StripeSetupBanner from '../components/StripeSetupBanner';

const VerificationBadge = ({ verification }) => {
  if (verification === 'trusted') {
    return (
      <Badge className="bg-amber-100 text-amber-800 gap-1">
        <Shield className="h-3 w-3" />
        Trusted Seller
      </Badge>
    );
  }
  if (verification === 'verified') {
    return (
      <Badge className="bg-green-100 text-green-800 gap-1">
        <BadgeCheck className="h-3 w-3" />
        Verified Seller
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      Unverified
    </Badge>
  );
};

const FoundingVendorBadge = ({ number }) => (
  <Badge
    data-testid="founding-vendor-badge"
    className="gap-1 bg-gradient-to-r from-amber-500 to-amber-700 text-white border-0 shadow-sm"
    title="Founding Vendor — discounted lifetime commission"
  >
    <Star className="h-3 w-3 fill-white" />
    Founding Vendor{typeof number === 'number' ? ` #${number}` : ''}
  </Badge>
);

// Vendor Overview Component
function VendorOverview() {
  const [vendor, setVendor] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [vendorRes, earningsRes, productsRes, ordersRes] = await Promise.all([
          axios.get(`${API}/vendors/me`),
          axios.get(`${API}/vendors/earnings`),
          axios.get(`${API}/vendors/products`),
          axios.get(`${API}/vendors/orders`)
        ]);
        setVendor(vendorRes.data);
        setEarnings(earningsRes.data);
        setProducts(productsRes.data.products || []);
        setOrders(ordersRes.data.orders || []);
      } catch (error) {
        console.error('Failed to fetch vendor data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        {['sk1','sk2','sk3','sk4'].map((k) => (
          <div key={k} className="h-32 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  if (!vendor) {
    return <VendorRegistration />;
  }

  if (vendor.status === 'pending') {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <Clock className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-xl font-heading font-medium mb-2">Pending Approval</h3>
          <p className="text-muted-foreground">
            Your vendor application is under review. We'll notify you once approved.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (vendor.status === 'rejected') {
    return (
      <Card>
        <CardContent className="py-16 text-center">
          <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
          <h3 className="text-xl font-heading font-medium mb-2">Application Rejected</h3>
          <p className="text-muted-foreground">
            Unfortunately, your vendor application was not approved. Please contact support for more information.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Vendor Status Banner */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Store className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-heading font-medium">{vendor.business_name}</h3>
              <p className="text-sm text-muted-foreground">{vendor.country}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap justify-end">
            {vendor.is_founding_vendor && (
              <FoundingVendorBadge number={vendor.founding_vendor_number} />
            )}
            <VerificationBadge verification={vendor.verification} />
            {vendor.verification === 'unverified' && (
              <p className="text-xs text-muted-foreground max-w-48">
                Complete more sales to get verified and build customer trust
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {vendor.is_founding_vendor && (
        <div
          data-testid="founding-vendor-banner"
          className="bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-200 rounded-lg p-4 flex items-start gap-3"
        >
          <Star className="h-5 w-5 text-amber-600 mt-0.5 fill-amber-500" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-900">
              You&apos;re a Founding Vendor{typeof vendor.founding_vendor_number === 'number' ? ` (#${vendor.founding_vendor_number})` : ''}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Discounted lifetime platform fee of{' '}
              <strong>{Math.round((vendor.commission_rate || 0.07) * 100)}%</strong> on every sale —
              locked in for as long as your store stays active.
            </p>
          </div>
        </div>
      )}

      {/* Share Your Store — vendor's #1 tool for driving traffic */}
      <ShareStoreToolkit vendor={vendor} />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                <Package className="h-6 w-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Products</p>
                <p className="text-2xl font-heading font-semibold">{products.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-secondary/10 rounded-xl flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-secondary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Orders</p>
                <p className="text-2xl font-heading font-semibold">{orders.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-heading font-semibold">${earnings?.total_sales?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Pending Payout</p>
                <p className="text-2xl font-heading font-semibold">${earnings?.pending_payout?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Orders</CardTitle>
          <Link to="/vendor/orders">
            <Button variant="ghost" size="sm">View All</Button>
          </Link>
        </CardHeader>
        <CardContent>
          {orders.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No orders yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order ID</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Total</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.slice(0, 5).map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="font-medium">{order.order_id}</TableCell>
                    <TableCell>{order.items.length} item(s)</TableCell>
                    <TableCell>${order.vendor_total?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">{order.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Vendor Registration Component
function VendorRegistration() {
  const [businessName, setBusinessName] = useState('');
  const [description, setDescription] = useState('');
  const [country, setCountry] = useState('');
  const [offerType, setOfferType] = useState('');
  const [agreedToTaxTerms, setAgreedToTaxTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [foundingStats, setFoundingStats] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`${API}/vendors/founding-program/stats`)
      .then((r) => setFoundingStats(r.data))
      .catch(() => setFoundingStats(null));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!agreedToTaxTerms) {
      toast.error('Please confirm you understand your tax responsibilities.');
      return;
    }
    setLoading(true);
    try {
      // Prepend offer type to description so admin sees it
      const fullDesc = offerType ? `[${offerType}] ${description}` : description;
      await axios.post(`${API}/vendors/register`, {
        business_name: businessName,
        description: fullDesc,
        country
      });
      toast.success('🎉 Your store is ready! Add your first listing to go live.');
      navigate('/vendor/products');
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="max-w-xl mx-auto">
      <CardHeader>
        <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 self-start">
          ✨ Founding Vendor — Free
        </div>
        {foundingStats && foundingStats.is_open && (
          <div
            data-testid="founding-spots-counter"
            className="rounded-lg border border-amber-300 bg-gradient-to-r from-amber-50 to-amber-100 px-4 py-3 mb-3"
          >
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wider text-amber-700 font-bold">
                  Limited Founding Spots
                </p>
                <p className="text-sm text-amber-900 mt-0.5">
                  <strong>{foundingStats.remaining}</strong> of {foundingStats.limit} spots left ·
                  lifetime <strong>{foundingStats.founding_commission_pct}%</strong> fee (vs {foundingStats.standard_commission_pct}%)
                </p>
              </div>
              <Star className="h-6 w-6 text-amber-500 fill-amber-400 shrink-0" />
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-amber-200 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-amber-500 to-amber-700"
                style={{ width: `${Math.min(100, (foundingStats.claimed / foundingStats.limit) * 100)}%` }}
              />
            </div>
          </div>
        )}
        <CardTitle>Almost there — set up your store</CardTitle>
        <CardDescription>For sellers, service providers, real estate agents, consultants and more. Free. No credit card. Live in seconds.</CardDescription>
        <div className="mt-3 text-xs text-muted-foreground bg-muted/40 rounded-md p-3 border">
          📋 Looking for our full screening process?{' '}
          <Link to="/vendor/apply" className="text-primary underline font-medium" data-testid="link-full-application">
            Submit a Vendor Application
          </Link>{' '}
          — for vendors who want to be reviewed in detail before going live. Also read our{' '}
          <Link to="/vendor/covenant" className="text-primary underline font-medium">
            Partnership Covenant
          </Link>.
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="businessName">Business Name</Label>
            <Input
              id="businessName"
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Your business name"
              required
              data-testid="vendor-business-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="offerType">What do you offer?</Label>
            <Select value={offerType} onValueChange={setOfferType} required>
              <SelectTrigger id="offerType" data-testid="vendor-offer-type">
                <SelectValue placeholder="Select what best describes your business" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Physical Products">🛍️ Physical Products (clothing, jewelry, gold, food, etc.)</SelectItem>
                <SelectItem value="Professional Services">💼 Professional Services (consulting, legal, financial, etc.)</SelectItem>
                <SelectItem value="Real Estate">🏠 Real Estate Services (brokerage, rentals, listings)</SelectItem>
                <SelectItem value="Oil & Gas / Commodities">⛽ Oil & Gas / Commodities</SelectItem>
                <SelectItem value="Construction & Building">🏗️ Construction & Building</SelectItem>
                <SelectItem value="Auto & Transportation">🚗 Auto & Transportation</SelectItem>
                <SelectItem value="Agriculture & Farming">🌾 Agriculture & Farming</SelectItem>
                <SelectItem value="Beauty & Skincare">💄 Beauty & Skincare</SelectItem>
                <SelectItem value="Wedding & Event Services">💍 Wedding & Event Services</SelectItem>
                <SelectItem value="Mixed / Multiple">🎯 Mixed / Multiple</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="description">Briefly describe your business</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-xs h-7 text-primary hover:text-primary hover:bg-primary/10"
                onClick={() => setDescription(VENDOR_TEMPLATE_SHORT)}
                data-testid="vendor-use-template-btn"
              >
                📋 Use template
              </Button>
            </div>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What do you sell or offer? Who are your typical customers?"
              rows={5}
              required
              data-testid="vendor-description"
            />
            <p className="text-xs text-muted-foreground">
              Not sure what to write? Tap <strong>Use template</strong> above for a faith-honoring starter, then swap in your own details.
              For the full About/Story version, see the{' '}
              <Link to="/vendor/help" className="text-primary underline">Vendor Help Center</Link>.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <Select value={country} onValueChange={setCountry} required>
              <SelectTrigger data-testid="vendor-country">
                <SelectValue placeholder="Select your country" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="US">United States</SelectItem>
                <SelectItem value="UK">United Kingdom</SelectItem>
                <SelectItem value="NG">Nigeria</SelectItem>
                <SelectItem value="KE">Kenya</SelectItem>
                <SelectItem value="GH">Ghana</SelectItem>
                <SelectItem value="ZA">South Africa</SelectItem>
                <SelectItem value="CA">Canada</SelectItem>
                <SelectItem value="AU">Australia</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 flex gap-3 items-start">
            <input
              id="taxAgreement"
              type="checkbox"
              className="mt-1 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500 cursor-pointer"
              checked={agreedToTaxTerms}
              onChange={(e) => setAgreedToTaxTerms(e.target.checked)}
              data-testid="vendor-tax-agreement"
              required
            />
            <label htmlFor="taxAgreement" className="text-xs text-amber-900 leading-relaxed cursor-pointer">
              I understand that <strong>I am solely responsible</strong> for collecting and remitting any sales tax, VAT, GST, or other transaction taxes on my sales, and for paying income tax on my earnings.
              I have read and agree to the{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-amber-700">
                Terms of Service
              </a>{' '}
              (Section 7 — Taxes).
            </label>
          </div>

          <Button type="submit" className="w-full" disabled={loading || !agreedToTaxTerms} data-testid="vendor-register-submit">
            {loading ? 'Setting up your store...' : 'Open My Store →'}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Free. No credit card. Live in seconds.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}

// Products Management Component
function VendorProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastAdded, setLastAdded] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [newProduct, setNewProduct] = useState({
    name: '', description: '', price: '', category: 'Other'
  });

  const resetForm = () => {
    setNewProduct({ name: '', description: '', price: '', category: 'Other' });
    setImageFile(null);
    setImagePreview(null);
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/vendors/products`);
      setProducts(response.data.products || []);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB');
      return;
    }
    setImageFile(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const handleAddProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // 1. Upload image (if provided)
      let imageUrls = [];
      if (imageFile) {
        const fd = new FormData();
        fd.append('file', imageFile);
        const up = await axios.post(`${API}/products/upload-image`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // Build absolute URL using the API base
        const apiBase = API.replace(/\/api$/, '');
        imageUrls = [`${apiBase}${up.data.url}`];
      }

      // 2. Create product
      const created = await axios.post(`${API}/products`, {
        name: newProduct.name,
        description: newProduct.description,
        price: parseFloat(newProduct.price),
        category: newProduct.category || 'Other',
        quantity: 100,
        images: imageUrls,
      });

      setLastAdded(created.data);
      setShowAddDialog(false);
      setShowSuccess(true);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      {/* Encouraging banner when 0 products */}
      {products.length === 0 && (
        <Card className="border-primary/30 bg-primary/5" data-testid="vendor-encourage-banner">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-primary/15 rounded-full flex items-center justify-center shrink-0">
              <Plus className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-medium text-foreground">You're almost done — just one more step!</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Add your first product or service and your store goes live instantly. Customers around the world will be able to find and buy from you.
              </p>
            </div>
            <Button onClick={() => setShowAddDialog(true)} data-testid="encourage-add-product">
              <Plus className="h-4 w-4 mr-2" />
              Add Listing
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-heading font-medium">My Listings</h2>
        <Dialog open={showAddDialog} onOpenChange={(o) => { setShowAddDialog(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-product-button">
              <Plus className="h-4 w-4 mr-2" />
              Add Product
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add a Product or Service</DialogTitle>
              <CardDescription>Takes under a minute. Goes live instantly.</CardDescription>
            </DialogHeader>
            <form onSubmit={handleAddProduct} className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={newProduct.name}
                  onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                  placeholder="e.g. Handcrafted Cross Necklace, Wedding Officiant Service, etc."
                  required
                  data-testid="product-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={newProduct.description}
                  onChange={(e) => setNewProduct({...newProduct, description: e.target.value})}
                  placeholder="What makes this product or service special?"
                  rows={3}
                  required
                  data-testid="product-description-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Photo (product image, service banner, or your logo)</Label>
                <Input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/jpg"
                  onChange={handleImageChange}
                  data-testid="product-image-file-input"
                />
                {imagePreview && (
                  <img src={imagePreview} alt="preview" className="mt-2 w-full h-40 object-cover rounded-lg border" />
                )}
                <p className="text-xs text-muted-foreground">JPEG, PNG, or WEBP — under 5MB</p>
              </div>
              <div className="space-y-2">
                <Label>Price ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={newProduct.price}
                  onChange={(e) => setNewProduct({...newProduct, price: e.target.value})}
                  placeholder="29.99"
                  required
                  data-testid="product-price-input"
                />
                <p className="text-xs text-muted-foreground">For services or hourly work, enter your starting price.</p>
              </div>
              <div className="space-y-2">
                <Label>Category (optional)</Label>
                <Select value={newProduct.category} onValueChange={(v) => setNewProduct({...newProduct, category: v})}>
                  <SelectTrigger data-testid="product-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Other">Other</SelectItem>
                    <SelectItem value="Jewelry & Accessories">Jewelry & Accessories</SelectItem>
                    <SelectItem value="Fashion & Apparel">Fashion & Apparel</SelectItem>
                    <SelectItem value="Shoes & Footwear">Shoes & Footwear</SelectItem>
                    <SelectItem value="Bags & Accessories">Bags & Accessories</SelectItem>
                    <SelectItem value="Hats & Headwear">Hats & Headwear</SelectItem>
                    <SelectItem value="Beauty & Skincare">Beauty & Skincare</SelectItem>
                    <SelectItem value="Home & Decor">Home & Decor</SelectItem>
                    <SelectItem value="Books & Media">Books & Media</SelectItem>
                    <SelectItem value="Agriculture & Farming">Agriculture & Farming</SelectItem>
                    <SelectItem value="Auto & Transportation">Auto & Transportation</SelectItem>
                    <SelectItem value="Construction & Building">Construction & Building</SelectItem>
                    <SelectItem value="Professional Services">Professional Services</SelectItem>
                    <SelectItem value="Electronics & Tech">Electronics & Tech</SelectItem>
                    <SelectItem value="Kids & Family">Kids & Family</SelectItem>
                    <SelectItem value="Food & Groceries">Food & Groceries</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground" data-testid="reserved-categories-note">
                  Oil &amp; Gas, Real Estate, and Gold are handled directly by Christ Kingdom trading desks and are not available for individual vendors.
                </p>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={submitting} className="w-full" data-testid="submit-product-button">
                  {submitting ? 'Publishing your product...' : 'Publish Product'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Success celebration dialog */}
      <Dialog open={showSuccess} onOpenChange={setShowSuccess}>
        <DialogContent className="max-w-md text-center" data-testid="product-live-dialog">
          <DialogHeader>
            <DialogTitle className="sr-only">Product Live</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <div>
              <h3 className="text-2xl font-heading font-semibold">🎉 Your product is LIVE!</h3>
              <p className="text-sm text-muted-foreground mt-2">
                <span className="font-medium text-foreground">{lastAdded?.name}</span> is now visible to shoppers worldwide. Keep going — more products = more sales.
              </p>
            </div>
            <div className="flex flex-col gap-2 pt-2">
              <Button
                onClick={() => { setShowSuccess(false); setShowAddDialog(true); }}
                className="w-full"
                data-testid="add-another-product"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Another Product
              </Button>
              {lastAdded && (
                <Link to={`/product/${lastAdded.product_id}`}>
                  <Button variant="outline" className="w-full" data-testid="view-live-product">
                    View My Product
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {products.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <Package className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-heading font-medium mb-2">No Products Yet</h3>
            <p className="text-muted-foreground mb-4">Add your first product — it goes live instantly</p>
            <Button onClick={() => setShowAddDialog(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Your First Product
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Price</TableHead>
              <TableHead>Stock</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.product_id} data-testid={`product-row-${product.product_id}`}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded bg-muted overflow-hidden">
                      <img src={product.images[0]} alt="" className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium">{product.name}</span>
                  </div>
                </TableCell>
                <TableCell>{product.category}</TableCell>
                <TableCell>${product.price.toFixed(2)}</TableCell>
                <TableCell>{product.quantity}</TableCell>
                <TableCell>
                  <Badge variant={product.is_active ? 'default' : 'secondary'}>
                    {product.is_active ? 'Active' : 'Inactive'}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="icon" onClick={() => handleDeleteProduct(product.product_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Main Vendor Dashboard Component
export default function VendorDashboard() {
  const { user } = useAuth();
  const location = useLocation();

  const navItems = [
    { path: '/vendor', label: 'Overview', icon: Store },
    { path: '/vendor/products', label: 'Products', icon: Package },
    { path: '/vendor/profile', label: 'Profile Preview', icon: Eye },
    { path: '/vendor/orders', label: 'Orders', icon: ShoppingCart },
    { path: '/vendor/earnings', label: 'Earnings', icon: DollarSign },
    { path: '/vendor/kyc', label: 'KYC Verification', icon: FileCheck },
    { path: '/vendor/help', label: 'Help & Guides', icon: HelpCircle, external: true },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-heading font-medium" data-testid="vendor-dashboard">
            Vendor Dashboard
          </h1>
        </div>

        {/* Persistent: shown until vendor enables Stripe payouts */}
        <StripeSetupBanner />

        {/* Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link key={item.path} to={item.path}>
                <Button 
                  variant={isActive ? 'default' : 'ghost'}
                  className={isActive ? '' : 'text-muted-foreground'}
                >
                  <Icon className="h-4 w-4 mr-2" />
                  {item.label}
                </Button>
              </Link>
            );
          })}
        </div>

        <Routes>
          <Route index element={<VendorOverview />} />
          <Route path="products" element={<VendorProducts />} />
          <Route path="profile" element={<VendorProfilePreview />} />
          <Route path="orders" element={<VendorOrders />} />
          <Route path="earnings" element={<VendorEarnings />} />
          <Route path="quotes" element={<QuotesManager scope="vendor" title="Quote Requests for Your Products" />} />
          <Route path="kyc" element={<VendorKYC />} />
        </Routes>
      </main>

      <Footer />
    </div>
  );
}

// Vendor Orders Component
function VendorOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/vendors/orders`);
        setOrders(response.data.orders || []);
      } catch (error) {
        console.error('Failed to fetch orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-heading font-medium">Orders</h2>
      
      {orders.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center">
            <ShoppingCart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-heading font-medium mb-2">No Orders Yet</h3>
            <p className="text-muted-foreground">Orders will appear here when customers purchase your products</p>
          </CardContent>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order ID</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Items</TableHead>
              <TableHead>Your Total</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {orders.map((order) => (
              <TableRow key={order.order_id}>
                <TableCell className="font-medium">{order.order_id}</TableCell>
                <TableCell>{new Date(order.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{order.items.map(i => i.name).join(', ')}</TableCell>
                <TableCell>${order.vendor_total?.toFixed(2)}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{order.status}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

// Vendor Earnings Component
function VendorEarnings() {
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingPayout, setRequestingPayout] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [connecting, setConnecting] = useState(false);

  const fetchStripeStatus = async () => {
    try {
      const r = await axios.get(`${API}/vendors/stripe/status`);
      setStripeStatus(r.data);
    } catch (e) {
      setStripeStatus({ connected: false, payouts_enabled: false });
    }
  };

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        const response = await axios.get(`${API}/vendors/earnings`);
        setEarnings(response.data);
      } catch (error) {
        console.error('Failed to fetch earnings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
    fetchStripeStatus();

    // If returning from Stripe onboarding, refresh status after short delay
    const params = new URLSearchParams(window.location.search);
    if (params.get('stripe') === 'return') {
      setTimeout(fetchStripeStatus, 1500);
    }
  }, []);

  const handleConnectStripe = async () => {
    setConnecting(true);
    try {
      const r = await axios.post(`${API}/vendors/stripe/connect`);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Payments not yet available. Please check back shortly.');
      setConnecting(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!payoutAmount || parseFloat(payoutAmount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (parseFloat(payoutAmount) > earnings.pending_payout) {
      toast.error('Amount exceeds available balance');
      return;
    }

    setRequestingPayout(true);
    try {
      await axios.post(`${API}/vendors/payout/request`, { amount: parseFloat(payoutAmount) });
      toast.success('Payout request submitted!');
      setPayoutAmount('');
      // Refresh earnings
      const response = await axios.get(`${API}/vendors/earnings`);
      setEarnings(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request payout');
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return <div className="h-64 bg-muted rounded-lg animate-pulse" />;
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-heading font-medium">Earnings</h2>

      {/* Stripe Connect status banner */}
      {stripeStatus?.payouts_enabled ? (
        <Card className="border-green-200 bg-green-50" data-testid="stripe-connected-banner">
          <CardContent className="p-5 flex items-center gap-4">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-medium text-green-900">Stripe is connected</h3>
              <p className="text-sm text-green-800">Your sales are automatically paid out to your bank. 10% marketplace commission is deducted.</p>
            </div>
          </CardContent>
        </Card>
      ) : stripeStatus?.connected ? (
        <Card className="border-yellow-200 bg-yellow-50" data-testid="stripe-onboarding-pending">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center shrink-0">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-medium text-yellow-900">Finish your Stripe onboarding</h3>
              <p className="text-sm text-yellow-800 mb-3">We need a few more details before you can receive payouts from sales.</p>
              <Button onClick={handleConnectStripe} disabled={connecting} size="sm" data-testid="continue-stripe-onboarding">
                {connecting ? 'Loading...' : 'Continue Onboarding →'}
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-primary/30 bg-primary/5" data-testid="connect-stripe-banner">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="w-10 h-10 bg-primary/15 rounded-full flex items-center justify-center shrink-0">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-heading font-medium">Connect Stripe to start receiving payouts</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Set up once (~5 min). Every sale automatically pays you after a 10% marketplace commission.
                Stripe handles taxes (1099) and payouts to your bank.
              </p>
              <Button onClick={handleConnectStripe} disabled={connecting} data-testid="connect-stripe-button">
                {connecting ? 'Loading...' : 'Connect with Stripe →'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Total Sales</p>
            <p className="text-3xl font-heading font-semibold text-primary">
              ${earnings?.total_sales?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Commission Rate</p>
            <p className="text-3xl font-heading font-semibold">
              {((earnings?.commission_rate || 0.10) * 100).toFixed(0)}%
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">Available for Payout</p>
            <p className="text-3xl font-heading font-semibold text-green-600">
              ${earnings?.pending_payout?.toFixed(2) || '0.00'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Where's my money? — payout explainer */}
      <Card data-testid="payout-explainer" className="border-blue-200 bg-blue-50/60">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-blue-900">
            <Clock className="h-5 w-5 text-blue-600" />
            Where&apos;s my money?
          </CardTitle>
          <CardDescription className="text-blue-800">
            How Stripe pays you — and when.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-blue-900">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-semibold text-blue-900">1. Buyer pays</p>
              <p className="text-xs text-blue-700 mt-1">Buyer is charged the full price at checkout.</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-semibold text-blue-900">2. Stripe splits instantly</p>
              <p className="text-xs text-blue-700 mt-1">{((earnings?.commission_rate || 0.10) * 100).toFixed(0)}% platform fee comes off the top. Stripe&apos;s ~2.9% + $0.30 processing fee is deducted from your share.</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-semibold text-blue-900">3. Stripe holds briefly</p>
              <p className="text-xs text-blue-700 mt-1">New Connect accounts: ~7-10 days for the first few payouts. Established accounts: 2-day rolling.</p>
            </div>
            <div className="bg-white rounded-lg p-3 border border-blue-200">
              <p className="font-semibold text-blue-900">4. Money in your bank</p>
              <p className="text-xs text-blue-700 mt-1">Stripe deposits directly. No invoicing, no waiting on us — payouts happen automatically.</p>
            </div>
          </div>
          <div className="bg-white border border-blue-200 rounded-md p-3 text-xs leading-relaxed">
            <p className="font-semibold mb-1">Why does Stripe hold the first payouts?</p>
            <p className="text-blue-800">Stripe uses the initial 7-10 day window to confirm there are no buyer chargebacks. After ~30 days of clean processing, this drops to a standard <strong>2-day rolling payout</strong> automatically. You don&apos;t need to do anything.</p>
          </div>
          <div className="bg-white border border-blue-200 rounded-md p-3 text-xs leading-relaxed">
            <p className="font-semibold mb-1">Where do I see exact payout dates?</p>
            <p className="text-blue-800">Log into your <a href="https://dashboard.stripe.com/payouts" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline font-medium">Stripe Dashboard → Payouts</a> using the email you connected. Every payout, its date, and the bank account it&apos;s going to are listed there.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Request Payout</CardTitle>
          <CardDescription>Enter the amount you'd like to withdraw</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4 max-w-md">
            <Input
              type="number"
              step="0.01"
              placeholder="Amount"
              value={payoutAmount}
              onChange={(e) => setPayoutAmount(e.target.value)}
              data-testid="payout-amount"
            />
            <Button onClick={handleRequestPayout} disabled={requestingPayout} data-testid="request-payout">
              {requestingPayout ? 'Processing...' : 'Request Payout'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


// Vendor KYC Verification Component
function VendorKYC() {
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [businessLicense, setBusinessLicense] = useState(null);
  const [governmentId, setGovernmentId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/vendors/kyc/me`);
      setKyc(res.data);
    } catch (e) {
      if (e.response?.status === 404) {
        setKyc({ no_profile: true });
      } else {
        console.error('Failed to fetch KYC status', e);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!businessLicense || !governmentId) {
      toast.error('Please upload both documents');
      return;
    }
    const formData = new FormData();
    formData.append('business_license', businessLicense);
    formData.append('government_id', governmentId);
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendors/kyc/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('KYC documents submitted for admin review');
      setBusinessLicense(null);
      setGovernmentId(null);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="h-40 bg-muted rounded-lg animate-pulse" />;
  }

  if (kyc?.no_profile) {
    return (
      <Card data-testid="kyc-no-profile">
        <CardContent className="py-12 text-center space-y-4">
          <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto" />
          <div>
            <h3 className="text-lg font-heading font-medium">Complete your Vendor Profile first</h3>
            <p className="text-sm text-muted-foreground mt-1">
              You need to register your business details before submitting KYC documents.
            </p>
          </div>
          <Link to="/vendor">
            <Button>Go to Vendor Setup</Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const status = kyc?.kyc_status || 'not_submitted';
  const statusBadge = {
    not_submitted: { text: 'Not Submitted', cls: 'bg-gray-100 text-gray-800' },
    submitted: { text: 'Under Review', cls: 'bg-blue-100 text-blue-800' },
    approved: { text: 'Approved', cls: 'bg-green-100 text-green-800' },
    rejected: { text: 'Rejected', cls: 'bg-red-100 text-red-800' },
  }[status];

  const canSubmit = status === 'not_submitted' || status === 'rejected';

  return (
    <div className="space-y-6" data-testid="vendor-kyc-page">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                KYC Verification
              </CardTitle>
              <CardDescription>
                Submit your Business License and Government ID to get your verified badge.
              </CardDescription>
            </div>
            <Badge className={statusBadge.cls} data-testid="kyc-status-badge">{statusBadge.text}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'submitted' && (
            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                Your documents were submitted on{' '}
                {kyc.submitted_at ? new Date(kyc.submitted_at).toLocaleDateString() : '—'}.
                An admin will review them within 1-2 business days.
              </div>
            </div>
          )}
          {status === 'approved' && (
            <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
              <div className="text-sm text-green-800">
                Your KYC has been approved. You are now a verified vendor!
              </div>
            </div>
          )}
          {status === 'rejected' && kyc.rejection_reason && (
            <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div className="text-sm text-red-800">
                <div className="font-medium mb-1">Your documents were rejected:</div>
                <div>{kyc.rejection_reason}</div>
                <div className="mt-2">Please re-submit clearer documents below.</div>
              </div>
            </div>
          )}

          {canSubmit && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="business-license">Business License (PDF, JPG, PNG, WEBP — max 8MB)</Label>
                <Input
                  id="business-license"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setBusinessLicense(e.target.files[0])}
                  data-testid="kyc-business-license-input"
                />
                {businessLicense && (
                  <p className="text-xs text-muted-foreground">{businessLicense.name}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="government-id">Government ID (PDF, JPG, PNG, WEBP — max 8MB)</Label>
                <Input
                  id="government-id"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp"
                  onChange={(e) => setGovernmentId(e.target.files[0])}
                  data-testid="kyc-government-id-input"
                />
                {governmentId && (
                  <p className="text-xs text-muted-foreground">{governmentId.name}</p>
                )}
              </div>
              <Button
                type="submit"
                disabled={submitting || !businessLicense || !governmentId}
                data-testid="kyc-submit-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                {submitting ? 'Uploading...' : 'Submit for Verification'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
